import React from "react";
import { Link } from "react-router-dom";
import { Globe, Smartphone, TrendingUp, ArrowRight } from "lucide-react";

export default function Aboutus() {
  const pillars = [
    {
      icon: <Globe size={28} className="text-blue-600 dark:text-cyan-400" />,
      title: "🌐 Website Development",
      description: "Crafting high-performance, custom websites using cutting-edge architectures like React, Next.js, PHP, WordPress, and Shopify."
    },
    {
      icon: <Smartphone size={28} className="text-indigo-600 dark:text-violet-400" />,
      title: "📱 App Development",
      description: "Engineering native and cross-platform mobile applications optimized flawlessly for iOS and Android environments."
    },
    {
      icon: <TrendingUp size={28} className="text-blue-600 dark:text-blue-400" />,
      title: "🚀 Digital Marketing",
      description: "Accelerating brand visibility and driving measurable business growth through smart, data-driven marketing strategies."
    }
  ];

  const workflowSteps = [
    { number: "01", title: "Discovery & Strategy", desc: "Understanding client goals, target audience, and architectural requirements." },
    { number: "02", title: "Design & Prototyping", desc: "User-centric interface wireframes and interactive visual prototypes." },
    { number: "03", title: "Engineering & Build", desc: "Writing clean, high-performance code across web, mobile, and backend frameworks." },
    { number: "04", title: "QA & Optimization", desc: "Rigorous cross-device testing, performance audits, and conversion optimization." },
    { number: "05", title: "Go-Live & Growth", desc: "Flawless deployment followed by data-driven marketing and 24/7 dedicated support." }
  ];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#030712] text-slate-900 dark:text-slate-200 font-body py-16 transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 space-y-16">
        
        {/* HEADER */}
        <div className="text-center max-w-3xl mx-auto space-y-4">
          <span className="text-xs font-bold uppercase tracking-widest text-blue-600 dark:text-cyan-400 bg-blue-50 dark:bg-cyan-500/10 px-3.5 py-1.5 rounded-full border border-blue-200 dark:border-cyan-500/20 font-heading">
            About Hickter Technology
          </span>
          <h1 className="font-heading text-4xl md:text-5xl font-extrabold text-slate-900 dark:text-white">
            Engineered to Solve <span className="text-gradient-cyan">Complex Digital Challenges</span>
          </h1>
          <p className="text-slate-600 dark:text-slate-400 text-sm md:text-base leading-relaxed font-medium">
            Hickter is a forward-thinking technology solutions company engineered to solve complex digital challenges. While our vision, mission, and core engineering values set our standard, our ultimate goal remains straightforward: <strong>We help businesses grow through modern websites, apps, and smart digital marketing.</strong>
          </p>
        </div>

        {/* CORE PILLARS */}
        <div className="space-y-8">
          <div className="text-center space-y-2">
            <span className="text-xs font-bold uppercase tracking-widest text-indigo-600 dark:text-violet-400 font-heading">What We Do</span>
            <h2 className="font-heading text-3xl font-extrabold text-slate-900 dark:text-white">Our Core Engineering Pillars</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {pillars.map((item, idx) => (
              <div key={idx} className="glass-card p-8 rounded-2xl space-y-4">
                <div className="p-3 rounded-2xl bg-slate-100 dark:bg-slate-900 w-fit border border-slate-200 dark:border-white/10">
                  {item.icon}
                </div>
                <h3 className="font-heading text-xl font-bold text-slate-900 dark:text-white">{item.title}</h3>
                <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed font-medium">{item.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* WORKFLOW ROADMAP */}
        <div className="space-y-8">
          <div className="text-center space-y-2">
            <span className="text-xs font-bold uppercase tracking-widest text-blue-600 dark:text-cyan-400 font-heading">Execution Standard</span>
            <h2 className="font-heading text-3xl font-extrabold text-slate-900 dark:text-white">How We Deliver Results</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {workflowSteps.map((m, idx) => (
              <div key={idx} className="glass-card p-5 rounded-2xl space-y-2 relative">
                <span className="text-2xl font-extrabold font-heading text-blue-600 dark:text-cyan-400">{m.number}</span>
                <h4 className="font-heading text-sm font-bold text-slate-900 dark:text-white">{m.title}</h4>
                <p className="text-slate-600 dark:text-slate-400 text-xs leading-relaxed font-medium">{m.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="glass-panel p-10 rounded-3xl text-center space-y-6 max-w-4xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-slate-900 dark:text-white">
            Let's Scale Your Digital Presence
          </h2>
          <p className="text-slate-600 dark:text-slate-300 text-sm max-w-xl mx-auto font-medium">
            Partner with Hickter to build modern websites, native mobile apps, and data-driven marketing campaigns.
          </p>
          <div className="flex justify-center gap-4">
            <Link to="/enquirynow" className="cyber-btn-primary inline-flex items-center gap-2 text-xs">
              <span>Discuss Your Project</span>
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>

      </div>
    </div>
  );
}
